import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Col, DatePicker, Form, Input, InputNumber, message, Modal,
  Row, Select, Space, Tag, Typography
} from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { fixedAssetsApi } from './fixedAssetsApi';
import {
  DEPRECIATION_METHODS, STATUS_COLOR, STATUS_LABEL,
  findAssetFormErrors, friendlyErrorFor, calculateWdv, formatRupee
} from './fixedAssetsHelpers';
import '../accounts.css';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Fixed Asset Add / Edit / View modal (Stage 6B.9D).
//
// Centered 1080-wide AntD Modal. The page owns Depreciation / Disposal
// / Delete actions so the MFA modal never stacks visually on this form.
// ─────────────────────────────────────────────────────────────────

const STATUS_VIEW_MODES = new Set(['view']);

// Reusable ledger select — fetches the lookup list once on open + on
// search. Falls back to the value's cached label when present.
function LedgerSelect({ value, onChange, disabled, subtype, placeholder }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();

  const fetcher = useCallback(async (q) => {
    setLoading(true);
    try {
      if (subtype) {
        return await fixedAssetsApi.ledgersBySubtype(subtype, { q, limit: 100 });
      }
      return await fixedAssetsApi.ledgerLookup(q || '', 100);
    } finally { setLoading(false); }
  }, [subtype]);

  const onOpen = useCallback(async (open) => {
    if (!open || options.length > 0) return;
    try { setOptions(await fetcher('')); } catch (_e) { /* swallow */ }
  }, [options.length, fetcher]);

  const onSearch = useCallback((text) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try { setOptions(await fetcher(text || '')); } catch (_e) { /* noop */ }
    }, 200);
  }, [fetcher]);

  return (
    <Select
      style={{ width: '100%' }}
      disabled={disabled}
      showSearch
      placeholder={placeholder || 'Search ledger…'}
      value={value || undefined}
      onChange={(v) => onChange(v || null)}
      onDropdownVisibleChange={onOpen}
      onSearch={onSearch}
      filterOption={false}
      loading={loading}
      allowClear
      notFoundContent={loading ? 'Loading…' : 'No ledgers'}
      options={options.map((o) => ({
        value: o.id,
        label: (
          <span>
            <code style={{ marginRight: 6, fontSize: 11, color: '#888' }}>{o.accountCode}</code>
            {o.accountName}
            <span style={{ marginLeft: 6, fontSize: 11, color: '#999' }}>
              · {o.accountType}
            </span>
          </span>
        )
      }))}
    />
  );
}

function defaultsFor(mode, record) {
  if (mode === 'add' || !record) {
    return {
      purchaseDate:       dayjs(),
      capitalizationDate: dayjs(),
      depreciationMethod: 'wdv',
      originalCost:       0,
      additionalCost:     0,
      salvageValue:       0
    };
  }
  return {
    assetName:                              record.assetName,
    categoryId:                             record.categoryId,
    assetLedgerAccountId:                   record.assetLedgerAccountId,
    accumulatedDepreciationLedgerAccountId: record.accumulatedDepreciationLedgerAccountId,
    depreciationExpenseLedgerAccountId:     record.depreciationExpenseLedgerAccountId,
    purchaseDate:       record.purchaseDate       ? dayjs(record.purchaseDate)       : null,
    capitalizationDate: record.capitalizationDate ? dayjs(record.capitalizationDate) : null,
    originalCost:                           Number(record.originalCost)   || 0,
    additionalCost:                         Number(record.additionalCost) || 0,
    salvageValue:                           Number(record.salvageValue)   || 0,
    depreciationMethod:                     record.depreciationMethod || 'wdv',
    depreciationRate:                       record.depreciationRate,
    usefulLifeYears:                        record.usefulLifeYears,
    location:                               record.location,
    serialNo:                               record.serialNo,
    purchaseInvoiceRef:                     record.purchaseInvoiceRef,
    remarks:                                record.remarks
  };
}

export default function FixedAssetModal({
  open, mode, record, onClose, onSaved
}) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);

  const isView   = STATUS_VIEW_MODES.has(mode);
  const readOnly = isView;
  const status   = record ? record.status : null;

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(defaultsFor(mode, record));
    // Fetch active categories for the dropdown.
    fixedAssetsApi.listCategories({ page: 1, pageSize: 200,
      filters: [{ field: 'is_active', op: 'eq', value: 'true' }] })
      .then((out) => setCategoryOptions(((out && out.rows) || [])))
      .catch(() => setCategoryOptions([]));
  }, [open, mode, record, form]);

  const saveAsset = useCallback(async () => {
    try {
      const headerValues = await form.validateFields();
      // Coerce dayjs → ISO strings before POSTing.
      const payload = {
        ...headerValues,
        purchaseDate:       headerValues.purchaseDate
          ? headerValues.purchaseDate.format('YYYY-MM-DD') : null,
        capitalizationDate: headerValues.capitalizationDate
          ? headerValues.capitalizationDate.format('YYYY-MM-DD') : null
      };
      const errs = findAssetFormErrors(payload);
      if (errs.length) {
        Modal.warning({
          title: 'Please fix these issues',
          content: <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13 }}>
            {errs.join('\n')}
          </pre>,
          okText: 'Close'
        });
        return;
      }
      setBusy(true);
      let saved;
      if (mode === 'add') {
        saved = await fixedAssetsApi.create(payload);
        message.success(`Asset ${saved.assetCode} created.`);
      } else if (mode === 'edit') {
        saved = await fixedAssetsApi.update(record.id, payload);
        message.success('Asset updated.');
      }
      onSaved && onSaved(saved);
    } catch (e) {
      if (e && e.errorFields) return; // antd Form validation
      message.error(friendlyErrorFor(e));
    } finally { setBusy(false); }
  }, [form, mode, record, onSaved]);

  const title = useMemo(() => {
    if (mode === 'add') return 'New Fixed Asset';
    const code = record && record.assetCode ? ` — ${record.assetCode}` : '';
    if (mode === 'view') return `View Fixed Asset${code}`;
    return `Edit Fixed Asset${code}`;
  }, [mode, record]);

  // Live WDV preview for the modal footer/header.
  const liveWdv = useMemo(() => {
    if (!record) return null;
    return calculateWdv({
      originalCost:            record.originalCost,
      additionalCost:          record.additionalCost,
      accumulatedDepreciation: record.accumulatedDepreciation
    });
  }, [record]);

  const footer = (
    <Space>
      {!readOnly ? (
        <Button type="primary" icon={<SaveOutlined />} loading={busy} onClick={saveAsset}>
          {mode === 'add' ? 'Create Asset' : 'Save Changes'}
        </Button>
      ) : null}
      <Button onClick={onClose}>Close</Button>
    </Space>
  );

  return (
    <Modal
      title={
        <span>
          {title}
          {status ? (
            <Tag color={STATUS_COLOR[status] || 'default'} style={{ marginInlineStart: 12 }}>
              {STATUS_LABEL[status] || status}
            </Tag>
          ) : null}
        </span>
      }
      open={open}
      onCancel={onClose}
      footer={footer}
      width={1080}
      centered
      destroyOnClose
      maskClosable={false}
      className="erp-accounts-modal"
      styles={{ body: { maxHeight: '74vh', overflowY: 'auto', padding: '12px 16px' } }}
    >
      <Form form={form} layout="vertical" preserve className="erp-accounts-form">
        <Row gutter={12}>
          <Col xs={24} md={6}>
            <Form.Item label="Asset Code">
              <Input value={(record && record.assetCode) || (mode === 'add' ? '(auto-generated)' : '')} disabled />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Asset Name" name="assetName"
              rules={[{ required: true, message: 'Asset name is required.' }]}>
              <Input maxLength={200} disabled={readOnly} placeholder="e.g. Toughening Furnace #1" />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Category" name="categoryId">
              <Select allowClear disabled={readOnly} placeholder="Select category"
                options={categoryOptions.map((c) => ({ value: c.id, label: `${c.categoryCode} · ${c.categoryName}` }))} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item label="Asset Ledger (Fixed Assets)" name="assetLedgerAccountId"
              rules={[{ required: true, message: 'Select a Fixed Asset ledger.' }]}>
              <_LedgerInput subtype="fixed_asset" disabled={readOnly}
                placeholder="Search Fixed Asset ledgers…" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Accumulated Depreciation Ledger" name="accumulatedDepreciationLedgerAccountId">
              <_LedgerInput disabled={readOnly}
                placeholder="Accumulated Depreciation ledger" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Depreciation Expense Ledger" name="depreciationExpenseLedgerAccountId">
              <_LedgerInput subtype="depreciation" disabled={readOnly}
                placeholder="Depreciation Expense ledger" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={6}>
            <Form.Item label="Purchase Date" name="purchaseDate"
              rules={[{ required: true, message: 'Purchase date is required.' }]}>
              <DatePicker style={{ width: '100%' }} disabled={readOnly} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Capitalization Date" name="capitalizationDate"
              rules={[{ required: true, message: 'Capitalization date is required.' }]}>
              <DatePicker style={{ width: '100%' }} disabled={readOnly} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Purchase Invoice Ref" name="purchaseInvoiceRef">
              <Input maxLength={100} disabled={readOnly} placeholder="Bill / GRN no." />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Serial No" name="serialNo">
              <Input maxLength={80} disabled={readOnly} placeholder="Vendor serial #" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={6}>
            <Form.Item label="Original Cost (₹)" name="originalCost"
              rules={[{ required: true, message: 'Original cost is required.' }]}>
              <InputNumber min={0} step={100} style={{ width: '100%' }} disabled={readOnly}
                formatter={(n) => n ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                parser={(s) => Number(String(s || '').replace(/[, ]/g, '')) || 0} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Additional Cost (₹)" name="additionalCost">
              <InputNumber min={0} step={100} style={{ width: '100%' }} disabled={readOnly}
                formatter={(n) => n ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                parser={(s) => Number(String(s || '').replace(/[, ]/g, '')) || 0} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Salvage Value (₹)" name="salvageValue">
              <InputNumber min={0} step={100} style={{ width: '100%' }} disabled={readOnly}
                formatter={(n) => n ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                parser={(s) => Number(String(s || '').replace(/[, ]/g, '')) || 0} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Accumulated Depr. (₹)">
              <Input disabled value={record
                ? Number(record.accumulatedDepreciation || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : '0.00'} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={6}>
            <Form.Item label="Depreciation Method" name="depreciationMethod"
              rules={[{ required: true, message: 'Depreciation method is required.' }]}>
              <Select disabled={readOnly}
                options={DEPRECIATION_METHODS.map((m) => ({ value: m.value, label: m.label }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Depreciation Rate (%)" name="depreciationRate">
              <InputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} disabled={readOnly} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Useful Life (years)" name="usefulLifeYears">
              <InputNumber min={0} step={1} style={{ width: '100%' }} disabled={readOnly} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Location" name="location">
              <Input maxLength={120} disabled={readOnly} placeholder="Factory / Warehouse" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24}>
            <Form.Item label="Remarks" name="remarks">
              <Input.TextArea rows={2} maxLength={2000} disabled={readOnly}
                placeholder="Optional notes — warranty, condition, etc." />
            </Form.Item>
          </Col>
        </Row>

        {record && liveWdv != null ? (
          <Alert type="info" showIcon style={{ marginTop: 6 }}
            message="Current Written-Down Value"
            description={
              <span>
                <Text strong>₹ {formatRupee(liveWdv)}</Text>
                <Text type="secondary" style={{ marginLeft: 10 }}>
                  (Original + Additional − Accumulated Depreciation)
                </Text>
              </span>
            } />
        ) : null}
      </Form>
    </Modal>
  );
}

// Tiny wrapper because AntD Form.Item expects its child to forward
// `value` + `onChange`. The LedgerSelect already does that, but
// nesting <Form.Item noStyle> drives AntD's controlled-input wiring.
function _LedgerInput(props) {
  return <LedgerSelect {...props} />;
}
